import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { Goal, GoalOperator, Timeframe, ApiResponse } from '@/types'

export interface GoalWithStatus extends Goal {
  status: 'met' | 'missed' | 'pending'
  metric_name?: string
  metric_unit?: string
  owner_name?: string
  owner_avatar?: string
  current_value?: number | null
}

// Fetch all goals
export function useGoals(filters?: { metric_id?: string; owner_id?: string; timeframe?: Timeframe }) {
  const params = new URLSearchParams()
  if (filters?.metric_id) params.set('metric_id', filters.metric_id)
  if (filters?.owner_id) params.set('owner_id', filters.owner_id)
  if (filters?.timeframe) params.set('timeframe', filters.timeframe)

  return useQuery({
    queryKey: ['goals', filters],
    queryFn: async (): Promise<GoalWithStatus[]> => {
      const url = params.toString() ? `/api/goals?${params}` : '/api/goals'
      const res = await fetch(url)
      const data: ApiResponse<GoalWithStatus[]> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch goals')
      return data.data!
    },
  })
}

// Fetch single goal
export function useGoal(id: string | undefined) {
  return useQuery({
    queryKey: ['goals', id],
    queryFn: async (): Promise<GoalWithStatus> => {
      const res = await fetch(`/api/goals/${id}`)
      const data: ApiResponse<GoalWithStatus> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to fetch goal')
      return data.data!
    },
    enabled: !!id,
  })
}

// Create goal
export function useCreateGoal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (goal: {
      metric_id: string
      owner_id: string
      timeframe: Timeframe
      is_recurring?: boolean
      period_start?: number
      period_end?: number
      operator: GoalOperator
      target_value: number
      target_min?: number
      target_max?: number
    }) => {
      const res = await fetch('/api/goals', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(goal),
      })
      const data: ApiResponse<Goal> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to create goal')
      return data.data!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Update goal
export function useUpdateGoal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...goal }: Partial<Goal> & { id: string }) => {
      const res = await fetch(`/api/goals/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(goal),
      })
      const data: ApiResponse<Goal> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to update goal')
      return data.data!
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['goals'] })
      queryClient.invalidateQueries({ queryKey: ['goals', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}

// Delete goal
export function useDeleteGoal() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`/api/goals/${id}`, { method: 'DELETE' })
      const data: ApiResponse<void> = await res.json()
      if (!data.success) throw new Error(data.error || 'Failed to delete goal')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goals'] })
      queryClient.invalidateQueries({ queryKey: ['scorecard'] })
    },
  })
}
